import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import ResponsiveView from "../../components/responsive/ResponsiveView";
import DataCard from "../../components/responsive/DataCard";
import { fetchMyShiftHistory, formatHours, shiftStatusBadgeClass, shiftStatusLabel } from "../../services/shiftService";
import styles from "./shifts.module.scss";

const RANGE_OPTIONS = [
    { label: "Today", value: "day" },
    { label: "This Week", value: "week" },
    { label: "This Month", value: "month" },
    { label: "This Year", value: "year" },
];

const ShiftHistory = () => {
    const [range, setRange] = useState("week");
    const [dateRange, setDateRange] = useState(null);
    const [shifts, setShifts] = useState([]);
    const [totalHours, setTotalHours] = useState(0);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        let res;
        if (dateRange && dateRange.length === 2 && dateRange[1]) {
            res = await fetchMyShiftHistory(null, dateRange[0].toISOString(), dateRange[1].toISOString());
        } else {
            res = await fetchMyShiftHistory(range);
        }
        setShifts(res?.data?.shifts || []);
        setTotalHours(res?.data?.totalHours || 0);
        setLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range]);

    const applyCustomRange = () => load();

    const stats = useMemo(
        () => ({
            shiftCount: shifts.length,
            breaksCount: shifts.reduce((sum, s) => sum + (s.breaks?.length || 0), 0),
        }),
        [shifts],
    );

    const clockInBody = (row) => (row.clockIn?.time ? new Date(row.clockIn.time).toLocaleString() : "-");
    const clockOutBody = (row) => (row.clockOut?.time ? new Date(row.clockOut.time).toLocaleString() : "-");
    const hoursBody = (row) => formatHours(row.totalHours);
    const breaksBody = (row) => (row.breaks?.length ? `${row.breaks.length} (${formatHours(row.breaks.reduce((s, b) => s + (b.hours || 0), 0))})` : "-");
    const statusBody = (row) => <span className={`${styles.badge} ${styles[shiftStatusBadgeClass(row.status)]}`}>{shiftStatusLabel(row.status)}</span>;

    const shiftCard = (row) => (
        <DataCard
            key={row._id}
            accent={row.status === "active" ? "green" : "slate"}
            title={formatHours(row.totalHours)}
            subtitle={row.clockIn?.time ? new Date(row.clockIn.time).toLocaleDateString() : ""}
            badge={statusBody(row)}
            fields={[
                { label: "Clock In", value: clockInBody(row) },
                { label: "Clock Out", value: clockOutBody(row) },
                { label: "Breaks", value: breaksBody(row) },
            ]}
        />
    );

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div className={styles.headerTitleRow}>
                    <div className={styles.headerIcon}>
                        <i className="pi pi-history" />
                    </div>
                    <div>
                        <h2>My Shift History</h2>
                        <p className={styles.subtitle}>Review your past shifts and hours worked</p>
                    </div>
                </div>
            </div>

            <div className={styles.statsRow}>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <i className="pi pi-clock" />
                    </div>
                    <div>
                        <div className={styles.statValue}>{formatHours(totalHours)}</div>
                        <div className={styles.statLabel}>Total Hours</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <i className="pi pi-calendar" />
                    </div>
                    <div>
                        <div className={styles.statValue}>{stats.shiftCount}</div>
                        <div className={styles.statLabel}>Shifts</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <i className="pi pi-pause" />
                    </div>
                    <div>
                        <div className={styles.statValue}>{stats.breaksCount}</div>
                        <div className={styles.statLabel}>Breaks Logged</div>
                    </div>
                </div>
            </div>

            <div className={styles.filtersSection}>
                <div className={styles.filterGroup}>
                    <label>Quick Range</label>
                    <div className={styles.quickRangeRow}>
                        {RANGE_OPTIONS.map((opt) => (
                            <Button
                                key={opt.value}
                                label={opt.label}
                                className={`${styles.quickRangeBtn} ${range === opt.value && !dateRange ? styles.activeRange : ""}`}
                                onClick={() => {
                                    setDateRange(null);
                                    setRange(opt.value);
                                }}
                            />
                        ))}
                    </div>
                </div>
                <div className={styles.filterGroup}>
                    <label htmlFor="dateRange">Custom Range</label>
                    <Calendar id="dateRange" value={dateRange} onChange={(e) => setDateRange(e.value)} selectionMode="range" readOnlyInput placeholder="Select date range" />
                </div>
                <div className={styles.filterGroup}>
                    <Button label="Apply" icon="pi pi-search" onClick={applyCustomRange} className={styles.primaryButton} />
                </div>
            </div>

            <ResponsiveView
                value={shifts}
                loading={loading}
                loadingText="Loading history..."
                emptyMessage="No shifts found for this period."
                storageKey="shiftHistory"
                renderCard={shiftCard}
            >
                <DataTable
                    value={shifts}
                    paginator
                    rows={20}
                    rowsPerPageOptions={[20, 50, 100]}
                    className="datatable-responsive custom-datatable"
                    emptyMessage="No shifts found for this period."
                >
                    <Column header="Clock In" body={clockInBody} />
                    <Column header="Clock Out" body={clockOutBody} />
                    <Column header="Total Hours" body={hoursBody} />
                    <Column header="Breaks" body={breaksBody} />
                    <Column header="Status" body={statusBody} />
                </DataTable>
            </ResponsiveView>
        </div>
    );
};

export default ShiftHistory;
